import React from "react";

import { ValuesStep } from "./FormSteps/ValuesStep";

import { Loading, Steps } from "@components";
import styles from "@styles/CashbackProgram.module.scss";
import { SuccessIcon } from "shared/components/Icons";
import { CashbackProgramAPI } from "../../../providers/CashbackProgramAPI";
import { Button } from "../../components/Button";
import { Drawer } from "../../components/Drawer";
import { Case, Switch } from "../../components/control-statements";
import { CashbackMessage } from "./CashbackMessage";
import { RemindStep } from "./FormSteps/RemindStep";
import { TypeStep } from "./FormSteps/TypeStep";
import { UserStorage } from "shared/tools/UserStorage";
import { EnvironmentStorage } from "shared/tools/EnvironmentStorage";

export class CashbackProgramFormDrawer extends React.Component {
  state = {
    isLoading: false,
    currentStep: null,
    cashbackProgram: {},
    hasErrors: true,
    enableRemind: false,
    showSuccess: false,
    steps: {
      TYPE: 1,
      VALUES: 2,
      RECEIVE: 3,
      REMIND: 4,
    },
    showPoints: false,
  };

  async setAsyncState(data) {
    return await new Promise((resolve) => this.setState(data, resolve));
  }

  async loadCashbackProgram() {
    const { brandId } = this.props;
    await this.setAsyncState({ isLoading: true });

    const response = await CashbackProgramAPI.getProgram(null, brandId);
    const program = response.getData(null);

    await this.setAsyncState(
      {
        isLoading: false,
        cashbackProgram: program || {},
      },
      this.setState({
        enableRemind: !!program?.expiration_message?.text,
        steps: {
          ...this.state.steps,
          ...(!!program?.expiration_message?.text && { REMIND_MESSAGE: 5 }),
        },
      })
    );
  }

  async componentDidMount() {
    this.loadCashbackProgram();

    const { steps } = this.state;
    const holdingId = await EnvironmentStorage.getEnvironmentHoldingId();
    const isCiaTC = holdingId === 811;

    if (isCiaTC || UserStorage.isGodMode) this.setState({ showPoints: true });

    this.setState({ currentStep: steps.TYPE });
  }

  async componentDidUpdate(prevProps, prevState) {
    const { props } = this;

    if (props.visible && prevProps.visible !== props.visible) {
      this.loadCashbackProgram().then(() => this.validateData());
    }

    if (prevState.currentStep !== this.state.currentStep) {
      this.validateData();
    }
  }

  nextStep() {
    const { currentStep, enableRemind, steps } = this.state;

    if (this.validateData()) {
      switch (currentStep) {
        case steps.TYPE:
          this.setState({ currentStep: steps.VALUES });
          break;
        case steps.VALUES:
          this.setState({ currentStep: steps.RECEIVE });
          break;
        case steps.RECEIVE:
          this.setState({ currentStep: steps.REMIND });
          break;
        case steps.REMIND:
          if (enableRemind) {
            this.setState({ currentStep: steps.REMIND_MESSAGE });
            break;
          }
          this.save();
          break;
        case steps.REMIND_MESSAGE:
          this.save();
          break;
        default:
          break;
      }
    }
  }

  validateData() {
    const { cashbackProgram, currentStep, steps, enableRemind } = this.state;

    switch (currentStep) {
      case steps.TYPE:
        if (!cashbackProgram?.type) {
          this.setState({ hasErrors: true });
          return false;
        }

        break;
      case steps.VALUES:
        if (
          cashbackProgram.percentage === null||
          cashbackProgram.percentage === undefined ||
          (cashbackProgram.percentage < 1 &&
            cashbackProgram?.type === "POINT") ||
          !cashbackProgram.max_gain ||
          !cashbackProgram.expiration_days
        ) {
          this.setState({ hasErrors: true });
          return false;
        }

        break;
      case steps.RECEIVE: {
        const { message } = cashbackProgram;

        if (message?.text) {
          const hasLink = message.text?.includes("<link>");
          const hasValorRecebido = message.text?.includes("<valorrecebido>");

          if (!(hasLink)) {
            this.setState({ hasErrors: true });
            return false;
          }
        }

        if (!message?.text) {
          this.setState({ hasErrors: true });
          return false;
        }

        this.setState({ hasErrors: false });
        return true;
      }
      case steps.REMIND: {
        const { expiration_message } = cashbackProgram;

        if (
          enableRemind &&
          (!expiration_message?.remind_days_before ||
            !expiration_message?.remind_hour)
        ) {
          this.setState({ hasErrors: true });
          return false;
        }

        this.setState({ hasErrors: false });
        return true;
      }
      case steps.REMIND_MESSAGE: {
        const { expiration_message } = cashbackProgram;

        const hasLink = expiration_message.text?.includes("<link>");
        const hasValorRecebido = expiration_message.text?.includes("<valorrecebido>");

        if (!(hasLink)) {
          this.setState({ hasErrors: true });
          return false;
        }

        this.setState({ hasErrors: false });
        return true;
      }
      default:
        break;
    }

    this.setState({ hasErrors: false });
    return true;
  }

  async save() {
    await this.setAsyncState({ isLoading: true });

    const { brandId } = this.props;
    const { cashbackProgram } = this.state;
    const {
      type,
      percentage,
      expiration_days,
      max_gain,
      min_order,
      message,
      expiration_message,
    } = cashbackProgram;

    const response = await CashbackProgramAPI.putProgram({
      type,
      brandId: brandId || null,
      percentage: Number(percentage),
      min_order: Number(min_order || 0),
      max_gain: Number(max_gain),
      expiration_days: Number(expiration_days),
      message: !message
        ? null
        : {
            type: `${message.type}`.toUpperCase(),
            text: message.text,
          },
      expiration_message: !expiration_message
        ? null
        : {
            type: `${expiration_message.type}`.toUpperCase(),
            text: expiration_message.text,
            remind_days_before: Number(expiration_message.remind_days_before),
            remind_hour: expiration_message.remind_hour,
          },
    });

    await this.setAsyncState({ isLoading: false });

    window.dispatchEvent(new CustomEvent("CASHBACK_PROGRAM_UPDATE"));

    if (response.getCode(500) !== 200) {
      alert({
        text: response.getErrors(["Ocorreu um erro ao salvar o programa!"])[0],
      });
      return;
    }

    this.setState({ showSuccess: true });
  }

  fillCashbackProgramByStep(step, data) {
    const { cashbackProgram, steps } = this.state;

    switch (step) {
      case steps.TYPE:
        cashbackProgram.type = data?.type;
        break;
      case steps.VALUES:
        cashbackProgram.percentage = data?.percentage;
        cashbackProgram.max_gain = data?.max_gain;
        cashbackProgram.min_order = data?.min_order;
        cashbackProgram.expiration_days = data?.expiration_days;
        break;
      case steps.REMIND:
        cashbackProgram.expiration_message = data?.expiration_message;
        break;
      default:
        break;
    }
    return cashbackProgram;
  }

  render() {
    const {
      currentStep,
      cashbackProgram,
      hasErrors,
      isLoading,
      enableRemind,
      steps,
      showSuccess,
    } = this.state;
    const { visible, programLabels, onClose, onSave } = this.props;

    return (
      <Drawer
        visible={visible}
        onClose={() => {
          this.setState({ currentStep: steps.TYPE });
          onClose?.();
        }}
        title={
          cashbackProgram.id
            ? "Editar programa de fidelidade"
            : "Configurar programa de fidelidade"
        }
        showBackButton={currentStep !== steps.TYPE && !showSuccess}
        backButton={() => {
          if (currentStep === steps.VALUES) {
            this.setState({ currentStep: steps.TYPE });
          } else if (currentStep === steps.RECEIVE) {
            this.setState({ currentStep: steps.VALUES });
          } else if (currentStep === steps.REMIND) {
            this.setState({ currentStep: steps.RECEIVE });
          } else if (currentStep === steps.REMIND_MESSAGE) {
            this.setState({ currentStep: steps.REMIND });
          }
        }}
        footer={
          showSuccess ? (
            <Button
              design="primary"
              className="w-100"
              text="Fechar"
              onClick={onSave}
            />
          ) : (
            <>
              <Switch value={currentStep}>
                <Case value={steps.TYPE}>
                  <Button
                    design="primary"
                    className={"w-100"}
                    onClick={() => this.nextStep()}
                    disabled={hasErrors}
                    text="Próximo"
                  />
                </Case>
                <Case value={steps.VALUES}>
                  <Button
                    design="primary"
                    className={"w-100"}
                    onClick={() => this.nextStep()}
                    disabled={hasErrors}
                    text="Próximo"
                  />
                </Case>
                <Case value={steps.RECEIVE}>
                  <Button
                    design="primary"
                    className={"w-100"}
                    onClick={() => this.nextStep()}
                    disabled={hasErrors}
                    text="Próximo"
                  />
                </Case>
                <Case value={steps.REMIND}>
                  <Button
                    design="primary"
                    className={"w-100"}
                    onClick={() => this.nextStep()}
                    disabled={hasErrors || isLoading}
                    text={
                      enableRemind
                        ? "Avançar"
                        : cashbackProgram.id
                        ? "Salvar edições"
                        : "Salvar programa"
                    }
                  />
                </Case>
                <Case value={steps.REMIND_MESSAGE}>
                  <Button
                    design="primary"
                    className={"w-100"}
                    onClick={() => this.nextStep()}
                    disabled={hasErrors || isLoading}
                    loading={isLoading}
                    text={
                      cashbackProgram.id ? "Salvar edições" : "Salvar programa"
                    }
                  />
                </Case>
              </Switch>
            </>
          )
        }
      >
        {isLoading ? (
          <div className={styles.loading}>
            <Loading />
          </div>
        ) : (
          !showSuccess && (
            <>
              <Steps
                steps={Object.keys(steps).length}
                currentStep={currentStep}
              />

              <div className="d-flex flex-column">
                <Switch value={currentStep}>
                  <Case value={steps.TYPE}>
                    <TypeStep
                      showPoints={this.state.showPoints}
                      cashbackProgram={cashbackProgram}
                      programLabels={programLabels}
                      onChange={(newProgram) => {
                        this.setState(
                          {
                            cashbackProgram: this.fillCashbackProgramByStep(
                              steps.TYPE,
                              newProgram
                            ),
                          },
                          this.validateData
                        );
                      }}
                    />
                  </Case>
                  <Case value={steps.VALUES}>
                    <ValuesStep
                      cashbackProgram={cashbackProgram}
                      programLabels={programLabels}
                      onChange={(newProgram) => {
                        this.setState(
                          {
                            cashbackProgram: this.fillCashbackProgramByStep(
                              steps.VALUES,
                              newProgram
                            ),
                          },
                          this.validateData
                        );
                      }}
                    />
                  </Case>
                  <Case value={steps.RECEIVE}>
                    <CashbackMessage
                      cashbackProgram={cashbackProgram}
                      programLabels={programLabels}
                      template={cashbackProgram.message?.text}
                      onChange={(message) => {
                        const newCashbackProgram = {
                          ...cashbackProgram,
                          message: {
                            type: "SMS",
                            text: message,
                          },
                        };

                        this.setState(
                          { cashbackProgram: newCashbackProgram },
                          this.validateData
                        );
                      }}
                    />
                  </Case>
                  <Case value={steps.REMIND}>
                    <RemindStep
                      cashbackProgram={cashbackProgram}
                      onChange={(newProgram) => {
                        this.setState(
                          {
                            cashbackProgram: this.fillCashbackProgramByStep(
                              steps.REMIND,
                              newProgram
                            ),
                          },
                          this.validateData
                        );
                      }}
                      enabledRemind={enableRemind}
                      onChangeRemind={(value) => {
                        this.setState({ enableRemind: value });

                        if (!!value) {
                          this.setState({
                            steps: { ...steps, REMIND_MESSAGE: 5 },
                          });
                          this.setState(
                            {
                              cashbackProgram: this.fillCashbackProgramByStep(
                                steps.REMIND,
                                {
                                  expiration_message: {
                                    text: "",
                                    remind_days_before: "",
                                    remind_hour: "",
                                  },
                                }
                              ),
                            },
                            this.validateData
                          );
                        } else {
                          const { REMIND_MESSAGE, ...remainingSteps } = steps;
                          this.setState(
                            {
                              steps: remainingSteps,
                              cashbackProgram: this.fillCashbackProgramByStep(
                                steps.REMIND,
                                { expiration_message: null }
                              ),
                            },
                            this.validateData
                          );
                        }
                      }}
                    />
                  </Case>
                  <Case value={steps.REMIND_MESSAGE}>
                    <CashbackMessage
                      cashbackProgram={cashbackProgram}
                      programLabels={programLabels}
                      template={cashbackProgram.expiration_message?.text}
                      onChange={(message) => {
                        const newCashbackProgram = {
                          ...cashbackProgram,
                          expiration_message: {
                            ...cashbackProgram.expiration_message,
                            type: "SMS",
                            text: message,
                          },
                        };

                        this.setState(
                          { cashbackProgram: newCashbackProgram },
                          this.validateData
                        );
                      }}
                    />
                  </Case>
                </Switch>
              </div>
            </>
          )
        )}

        {showSuccess && (
          <div className={styles.success}>
            <div className={styles.successIcon}>
              <SuccessIcon />
            </div>
            <p>As alterações foram salvas.</p>
          </div>
        )}
      </Drawer>
    );
  }
}
